import { useCallback, useEffect, useRef } from 'react'
import { useReducedMotion } from 'motion/react'

interface UseSnapCarouselParams {
  /** Reads the track and updates derived state; coalesced to one call per frame. */
  sync: (root: HTMLDivElement) => void
  resize?: 'window' | 'observer'
  /** Runs on every raw scroll, before the coalesced `sync`. */
  onScroll?: () => void
  onScrollEnd?: () => void
  gap?: number
  beforeProgrammaticScroll?: () => void
}

// Must match the track's Tailwind `gap-*` class (all consumers use `gap-4` = 16px)
// so one `scrollByCard` step advances exactly one card.
const DEFAULT_GAP_PX = 16

/**
 * Shared behaviour for the experiment's edge-aligned scroll-snap carousels
 * (ValuePromise / AiLabs / Instructors): the `scrollRef`, the rAF-coalesced
 * scroll+resize observation, and the scroll primitives. Each section keeps its
 * own markup and renders whatever `sync` derives; loop/auto/keyboard/nav-state
 * stay in the consumers that need them.
 */
export function useSnapCarousel({
  sync,
  resize = 'window',
  onScroll,
  onScrollEnd,
  gap = DEFAULT_GAP_PX,
  beforeProgrammaticScroll,
}: UseSnapCarouselParams) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | undefined>(undefined)
  // Pending flag kept separate from the rAF id so the guard never depends on
  // requestAnimationFrame being asynchronous (a synchronous/reentrant rAF would
  // otherwise clobber the id back to non-null and wedge the guard permanently).
  const pendingRef = useRef(false)
  const prefersReducedMotion = useReducedMotion()

  // Latest callbacks in refs so the listener effect subscribes once.
  const syncRef = useRef(sync)
  syncRef.current = sync
  const onScrollRef = useRef(onScroll)
  onScrollRef.current = onScroll
  const onScrollEndRef = useRef(onScrollEnd)
  onScrollEndRef.current = onScrollEnd
  const beforeScrollRef = useRef(beforeProgrammaticScroll)
  beforeScrollRef.current = beforeProgrammaticScroll

  const scheduleSync = useCallback(() => {
    if (pendingRef.current) return
    pendingRef.current = true
    rafRef.current = requestAnimationFrame(() => {
      pendingRef.current = false
      rafRef.current = undefined
      const root = scrollRef.current
      if (root) syncRef.current(root)
    })
  }, [])

  useEffect(() => {
    const root = scrollRef.current
    if (!root) return undefined
    syncRef.current(root)

    const handleScroll = () => {
      onScrollRef.current?.()
      scheduleSync()
    }
    const handleScrollEnd = () => onScrollEndRef.current?.()

    root.addEventListener('scroll', handleScroll, { passive: true })
    root.addEventListener('scrollend', handleScrollEnd)

    let resizeObserver: ResizeObserver | undefined
    if (resize === 'observer') {
      resizeObserver = new ResizeObserver(scheduleSync)
      resizeObserver.observe(root)
    } else {
      window.addEventListener('resize', scheduleSync)
    }

    return () => {
      root.removeEventListener('scroll', handleScroll)
      root.removeEventListener('scrollend', handleScrollEnd)
      if (resizeObserver) resizeObserver.disconnect()
      else window.removeEventListener('resize', scheduleSync)
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
  }, [scheduleSync, resize])

  const scrollByCard = useCallback(
    (direction: 1 | -1) => {
      const el = scrollRef.current
      if (!el) return
      // Assumes the track's first child is an HTMLElement (for offsetWidth).
      const firstCard = el.children.item(0) as HTMLElement | null
      const step = (firstCard?.offsetWidth ?? 0) + gap
      beforeScrollRef.current?.()
      el.scrollBy({
        left: direction * step,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      })
    },
    [gap, prefersReducedMotion],
  )

  // Scroll to an absolute `scrollLeft` offset (not a direction).
  const scrollToOffset = useCallback(
    (left: number, behavior?: ScrollBehavior) => {
      const el = scrollRef.current
      if (!el) return
      beforeScrollRef.current?.()
      el.scrollTo({
        left,
        behavior: behavior ?? (prefersReducedMotion ? 'auto' : 'smooth'),
      })
    },
    [prefersReducedMotion],
  )

  return { scrollRef, prefersReducedMotion, scrollByCard, scrollToOffset }
}
